<template lang="pug">
div(class='xwrap')
  div(class='map-controls')
    s-sites-picker(
      :clearable='false'
      label='Выберите сайт'
      v-model='site'
      return-object
      :filter="v => v.complex !== null"
      solo
    )
  div(class='map' ref='map')
  v-dialog(v-model='editDialogShown' max-width='600px')
    v-card
      v-card-title
        span(class='headline') {{ dialogTitle }}
      v-container(grid-list-xl)
        v-layout(wrap)
          v-flex(xs6)
            v-text-field(
              :error='errors.has("name")'
              :error-messages='errors.first("name")'
              v-model='model.name'
              label='Название'
            )
          v-flex(xs6)
            s-mark-types-picker(
              :error='errors.has("mark_type")'
              :error-messages='errors.first("mark_type")'
              :filter='v => v.site_id == site.id'
              v-model='model.mark_type_id'
            )
          v-flex(xs12)
            v-text-field(
              :error='errors.has("address")'
              :error-messages='errors.first("address")'
              :hint='coordinatesHint'
              :loading='busy'
              rows='2'
              v-model='model.address'
              @click:append='updateModelAddress'
              append-icon="autorenew"
              label='Адрес'
              persistent-hint
            )
          v-flex(xs12)
            v-text-field(
              :error='errors.has("description")'
              :error-messages='errors.first("description")'
              v-model='model.description'
              label='Описание'
            )
      v-card-actions
        template(v-if='model.id')
          v-btn(flat @click="onDelete") Удалить
        v-spacer
        v-btn(flat @click="undoEdit") Отмена
        v-btn(color="primary" flat @click="saveChanges") Сохранить
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import 'leaflet.fullscreen'
import 'leaflet.fullscreen/Control.FullScreen.css'

import 'leaflet-control-geocoder'

import { mapActions, mapGetters } from 'vuex'
import { getGeocoderResults } from '@/utils'
import { get } from 'lodash'

const createIcon = props => {
  return L.icon({
    iconUrl: props.icon_url,
    iconSize: [+props.width, +props.height],
    iconAnchor: [+props.offcet_x, +props.offcet_y]
  })
}

export default {
  data: () => ({
    markers: {},
    model: {
      geo_lat: null,
      geo_lon: null
    },
    editDialogShown: false,
    busy: false,
    mapCenter: {
      geo_lat: 55.796289,
      geo_lon: 49.104332,
      zoom: 13
    },
    site: {},
    dialogTitle: null
  }),

  mounted() {
    this.createMap()
  },

  destroyed() {
    this.destroyMap()
  },

  methods: {
    createMap() {
      this.destroyMap()

      /* eslint-disable-next-line */
      this.map = L.map(this.$refs.map, { fullscreenControl: true })
      this.map.setView(
        [this.mapCenter.geo_lat, this.mapCenter.geo_lon],
        this.mapCenter.zoom
      )

      this.createBaseLayer()
      this.geocoder = new L.Control.Geocoder()

      this.map.on('click', event => {
        L.DomEvent.stopPropagation(event)
        this.onMapClick(event)
      })

      this.updateAll()
    },

    createBaseLayer() {
      /* eslint-disable-next-line */
      L.tileLayer(
        'https://b.tiles.mapbox.com/v4/{id}/{z}/{x}/{y}@2x.png?access_token=pk.eyJ1IjoicnVzdGVtLWtoYWtpbXp5YW5vdiIsImEiOiJjanV4eWk1bHowY3FvNDNwaWdpNmdsd3RqIn0.3rMYh-1stbPD8wS1lNeD3A',
        {
          attribution: '',
          maxZoom: 18,
          id: 'mapbox.satellite'
        }
      ).addTo(this.map)
    },

    destroyMap() {
      if (this.map) {
        this.map.remove()
        this.map = null
      }
    },

    createMarker(placement) {
      if (
        null === placement.mark_type ||
        null === placement.mark_type.height ||
        null === placement.mark_type.width ||
        null === placement.mark_type.icon_url
      ) {
        return
      }

      const icon = createIcon(placement.mark_type)
      const marker = L.marker([placement.geo_lat, placement.geo_lon], {
        icon,
        draggable: true
      })
      
      marker.on('click', () => {
        this.model = placement
        this.editDialogShown = true
        this.dialogTitle = 'Редактирование маркера'
      })

      marker.on('dragend', async () => {
        setTimeout(async () => {
          if (await this.$confirm('Изменить локацию маркера?')) {
            const { lat, lng } = marker.getLatLng()

            this.model = placement
            this.model.geo_lat = lat
            this.model.geo_lon = lng

            // TODO: адрес может и не обновиться
            await this.updateModelAddress()

            this.saveChanges()
          } else {
            // Вернем на место, где был изначально
            const { geo_lat, geo_lon } = placement
            marker.setLatLng([geo_lat, geo_lon])
          }
        })
      })

      this.markers[placement.id] = marker
      marker.addTo(this.map)
    },

    undoEdit() {
      this.editDialogShown = false
      this.flushModel()
    },

    flushModel() {
      this.model = {}
    },

    onMapClick(event) {
      if (!this.site.complex.id) return

      const { lat, lng } = this.getCoordinates(event)
      this.busy = true

      this.geocoderService.reverse(
        { lat, lng },
        this.map.getZoom(),
        results => {
          const address = this.getAddress(results)
          this.$set(this.model, 'address', address)
          this.busy = false
        }
      )

      this.flushModel()

      this.model.geo_lat = lat
      this.model.geo_lon = lng
      this.model.complex_id = this.site.complex.id

      this.editDialogShown = true
      this.dialogTitle = 'Создать маркер'
    },

    updateModelAddress() {
      return new Promise(resolve => {
        if (this.model.id && !this.busy) {
          this.busy = true

          this.geocoderService.reverse(
            { lat: this.model.geo_lat, lng: this.model.geo_lon },
            this.map.getZoom(),
            results => {
              const address = this.getAddress(results)
              this.$set(this.model, 'address', address)
              this.busy = false
              resolve()
            }
          )
        }
      })
    },

    getAddress: getGeocoderResults,

    getCoordinates(event) {
      return this.map.mouseEventToLatLng(event.originalEvent)
    },

    forceUpdateAllMarkers() {
      this.updateAll()
    },

    async onDelete() {
      if (!(await this.$confirm('Вы уверены?'))) return

      const { id } = this.model
      this.editDialogShown = false

      try {
        await this.destroy({ id })
      } catch (e) {
        this.$root.showSnack('Ошибка удаления')
      }

      this.updateAll()
    },

    async saveChanges() {
      try {
        if (this.model.id) {
          await this.update({
            id: this.model.id,
            data: this.model
          })
        } else {
          await this.create({
            data: this.model
          })
        }

        this.$root.showSnack('Маркер сохранен')
        this.editDialogShown = false
        this.model = {}

        this.forceUpdateAllMarkers()
      } catch (e) {
        this.$showServerErrors(e)
      }
    },

    async updateAll() {
      try {
        const { data } = await this.fetchList()

        // Сделать нормальное обновление данных
        Object.keys(this.markers).forEach(key => {
          this.markers[key].remove()
        })

        data
          .filter(marker =>
            this.site.complex.id
              ? this.site.complex.id === marker.complex_id
              : 1
          )
          .forEach(this.createMarker)
      } catch (error) {
        this.$showServerErrors(error)
      }
    },

    ...mapActions('content/infrastructure', [
      'fetchList',
      'update',
      'create',
      'destroy'
    ])
  },

  computed: {
    geocoderService() {
      return this.geocoder.options.geocoder
    },

    coordinatesHint() {
      return `Координаты: ${this.model.geo_lat}, ${this.model.geo_lon}`
    },

    ...mapGetters('content/infrastructure', ['list', 'isLoading']),
    ...mapGetters({ sitesList: 'dictionary/sites/list' })
  },

  watch: {
    // Выбрать первый комплекс
    // по умолчанию
    sitesList(sites = []) {
      const complexId = get(sites, '[0].complex_id', false)
      if (complexId) {
        this.site = sites[0]
      } else this.site = {}
    },

    site: {
      deep: true,
      handler(site) {
        const complex = { ...site.complex }
        if (!this.map) return
        this.complexMarkers || (this.complexMarkers = {})
        if (complex.logo_url && !this.complexMarkers[complex.id]) {
          this.complexMarkers[complex.id] = L.marker(
            [complex.lat, complex.lon],
            {
              icon: L.icon({
                iconUrl: complex.logo_url,
                iconSize: [100, null],
                iconAnchor: [50, 50]
              })
            }
          )
            .addTo(this.map)
            // Не будем открывать всплывашку при клике
            // на маркер комплекса
            .on('click', ev => ev.originalEvent.stopPropagation())
        }

        this.map.setView([complex.lat, complex.lon], 13)
        this.updateAll()
      }
    }
  }
}
</script>

<style scoped>
.map {
  height: 100%;
  cursor: crosshair;
}
.map >>> .leaflet-pane {
  z-index: 2;
}
.map >>> .leaflet-top,
.map >>> .leaflet-bottom {
  z-index: 3;
}
.xwrap {
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  position: absolute;
}
.map-controls {
  right: 12px;
  top: 12px;
  z-index: 10;
  position: absolute;
}
</style>
